import { useDispatch, useSelector } from "react-redux";
import { ButtonBack, InputField, Layout } from "../../../components"
import { RootState } from "../../app/mainReducer";
import { AssinaturaRecebedorField, BotaoFinalizarEntrega, ModalObservacoesGrupo, NotaEntregaFormSection, Temporizador } from "../components"
import QuestionarioSatisfacao from "../components/QuestionarioSatisfacao";
import useRegistroEntregaRegras from "../../../hooks/useRegistroEntregaRegras"
import useEntregaDaRota from "../../../hooks/useEntregaDaRota";
import useRedirecionaAposConcluirEntrega from "../../../hooks/useRedirecionaAposConcluirEntrega";
import { useMemo, useState } from 'react';
import { Center, Flex, Heading, Text } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { entregaActions } from "../reducer";
import type { Carga } from "../../carga/types";
import { buscaCoordenadasParaEntrega } from "../../../utils/geolocalizacao";
import config from "../../../utils/config";
import { resolveQuestionarioSatisfacaoHabilitado } from "../../../utils/questionarioSatisfacaoRegras";
import { RespostaQuestionarioSatisfacaoItem } from "../types/questionario_satisfacao";
import { questionarioSatisfacaoActions } from "../../questionario-satisfacao/reducer";
import { montarGestoesDaEntrega, novoFormularioEntregaGrupo, validarFormularioEntregaGrupo } from "../utils";
import { notaEntregue } from "../utils/montarGestoesDaEntrega";

const RegistrarEntregaGrupo = () => {
  const gestao_entrega_temporario = useSelector((state: RootState) => state.entrega.gestao_entrega_temporario);
  const dispatch = useDispatch();
  const { j14_ukey, carga, entrega } = useEntregaDaRota();

  useRedirecionaAposConcluirEntrega(entrega, carga, j14_ukey);
  const e_retira = carga?.e_retira == '1';

  const cpfMotorista = useSelector((state: RootState) => state.login.parceiro?.cpf_cnpj);
  const pesquisaQuestionario = useSelector((state: RootState) => state.questionarioSatisfacao?.pesquisa);
  const clientesQueResponderam = useSelector(
    (state: RootState) => state.questionarioSatisfacao?.clientesQueResponderamQuestionario ?? []
  );
  const [respostasQuestionario, setRespostasQuestionario] = useState<RespostaQuestionarioSatisfacaoItem[]>([]);

  const [openModalObservacoes, setOpenModalObservacoes] = useState(false);
  const [openedModalObservacoes, setOpenedModalObservacoes] = useState(false);
  const regrasEntrega = useRegistroEntregaRegras();
  const valoresIniciais = useMemo(() => (entrega ? novoFormularioEntregaGrupo(entrega) : null), [entrega?.chave]);

  const a03Cliente = entrega?.notas[0]?.cliente_ukey as string | undefined;
  const clienteJaRespondeuLocal = !!a03Cliente && clientesQueResponderam.includes(a03Cliente);
  const clienteJaRespondeuApi = (entrega?.notas ?? []).some((nota) => nota.questionario_ja_respondido == 1);

  const mostrarQuestionario =
    resolveQuestionarioSatisfacaoHabilitado(cpfMotorista) &&
    !!pesquisaQuestionario &&
    !clienteJaRespondeuApi &&
    !clienteJaRespondeuLocal;

  const questionarioCompleto = () => {
    if (!mostrarQuestionario || !pesquisaQuestionario) return true;
    const obrigatorias = (pesquisaQuestionario.perguntas ?? []).filter(
      (p) => p.tipo_resposta == "estrelas" && (p.obrigatoria ?? 1) == 1
    );
    return obrigatorias.every((p) =>
      respostasQuestionario.some((r) => r.r11_ukey == p.ukey && !!r.r12_ukey)
    );
  };

  return (
    <Layout showFooter={true} isLoading={false} title={
      <>
        <ButtonBack />
        <Temporizador />
      </>
    }>
      <Center mt={8}>
        <Heading>Registrar Entrega</Heading>
      </Center>
      <Center mx={4} mb={6} mt={2} textAlign="center">
        <Text fontWeight="bold">{entrega?.fantasias}</Text>
      </Center>

      <Flex px={4} width="full" direction="column">
        {entrega && valoresIniciais && gestao_entrega_temporario && <Formik
          key={entrega.chave}
          initialValues={valoresIniciais}
          onSubmit={async (val, { setErrors }) => {
            const algumaEntregue = val.notas.some(notaEntregue);

            const erros = validarFormularioEntregaGrupo(val, regrasEntrega, {
              devolucaoExigirApenasFoto2: config.devolucaoExigirApenasFoto2,
              modalJaAberto: openedModalObservacoes && !e_retira,
              motivoTextoLivre: config.motivoDevolucaoTextoLivre,
            });
            if (erros) {
              setErrors(erros);
              return;
            }

            if (mostrarQuestionario && algumaEntregue && !questionarioCompleto()) {
              dispatch(entregaActions.setError("Responda todas as perguntas de avaliação com estrelas."));
              return;
            }

            if (!openedModalObservacoes && !e_retira) {
              setOpenModalObservacoes(true);
              return;
            }

            const coordenadas = e_retira ? { latitude: 0, longitude: 0 } : await buscaCoordenadasParaEntrega(gestao_entrega_temporario);

            if (!coordenadas) {
              dispatch(entregaActions.setError("Não foi possível obter a geolocalização. Verifique se o GPS está ativo, conceda permissão ao navegador e tente novamente ao ar livre."));
              return;
            }

            if (mostrarQuestionario && algumaEntregue && a03Cliente) {
              dispatch(questionarioSatisfacaoActions.marcaClienteRespondeuQuestionario(a03Cliente));
            }

            dispatch(entregaActions.registraNovoGestaoEntregaGrupo({
              gestoes: montarGestoesDaEntrega(gestao_entrega_temporario, e_retira ? { ...val, teve_devolucao_mercadoria: 0 } : val, {
                latitude: coordenadas.latitude,
                longitude: coordenadas.longitude,
                respostas_questionario: mostrarQuestionario && algumaEntregue ? respostasQuestionario : [],
              }),
              carga: carga as Carga,
            }))
          }}
        >
          {({ values, handleSubmit, isSubmitting }) => {
            const algumaEntregue = values.notas.some(notaEntregue);
            return (
              <Form>
                <InputField
                  label="Nome do Recebedor"
                  placeholder={regrasEntrega.nomeRecebedorObrigatorio ? "Nome e sobrenome do recebedor" : "Nome do Recebedor"}
                  name="nome_recebedor"
                />

                {entrega.notas.map((nota, indice) => (
                  <NotaEntregaFormSection
                    key={nota.j10_ukey}
                    index={indice}
                    nota={nota}
                    devolucaoExigirApenasFoto2={config.devolucaoExigirApenasFoto2}
                    motivoTextoLivre={config.motivoDevolucaoTextoLivre}
                    permiteRecusa={entrega.notas.length > 1}
                  />
                ))}

                {mostrarQuestionario && algumaEntregue && (
                  <QuestionarioSatisfacao onChange={setRespostasQuestionario} />
                )}

                {regrasEntrega.assinaturaRecebedorObrigatoria && algumaEntregue && <AssinaturaRecebedorField />}

                {!e_retira && <ModalObservacoesGrupo values={values} onSubmited={handleSubmit} onOpenModal={openModalObservacoes} onOpenedModal={() => setOpenedModalObservacoes(true)} isLoading={isSubmitting} />}

                <BotaoFinalizarEntrega isLoading={isSubmitting} />
              </Form>
            );
          }}
        </Formik>
        }
      </Flex>
    </Layout>
  )
}

export default RegistrarEntregaGrupo;
